import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPin, Lock, Save, Trash2, Loader2, Plus, Upload, Download } from "lucide-react";
import { getAdminZips, saveAdminZip, deleteAdminZip, bulkImportZips } from "@/lib/api";

const FIELDS = [
  { k: "storage", label: "Storage $/sqft", step: 0.05 },
  { k: "labor", label: "Labor $/hr (basic)", step: 0.25 },
  { k: "inbound", label: "Inbound $/plt", step: 0.25 },
  { k: "outbound", label: "Outbound $/plt", step: 0.25 },
];

const EMPTY = { zip: "", label: "", storage: 0.95, labor: 18.5, inbound: 7.5, outbound: 11.0 };

export default function ZipRateManager({ passcode, laborUplift = 0 }) {
  const [zips, setZips] = useState([]);
  const [draft, setDraft] = useState({});
  const [add, setAdd] = useState(EMPTY);
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const d = await getAdminZips(passcode);
      setZips(d.zips);
      const dr = {};
      d.zips.forEach((z) => (dr[z.zip] = { ...z }));
      setDraft(dr);
    } catch (e) {
      toast.error("Could not load zip rates");
    }
  };

  useEffect(() => {
    if (passcode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode]);

  const num = (v) => (v === "" ? "" : Math.max(0, +v));

  const onAdd = async () => {
    if (`${add.zip}`.replace(/\D/g, "").length !== 5) {
      toast.error("Enter a valid 5-digit zipcode");
      return;
    }
    setAdding(true);
    try {
      await saveAdminZip(passcode, add);
      toast.success(`Rates locked for ${add.zip}`);
      setAdd(EMPTY);
      await load();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setAdding(false);
    }
  };

  const onSave = async (zip) => {
    setBusy(zip);
    try {
      await saveAdminZip(passcode, draft[zip]);
      toast.success(`${zip} updated`);
      await load();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy("");
    }
  };

  const onDelete = async (zip) => {
    setBusy(zip);
    try {
      await deleteAdminZip(passcode, zip);
      toast.success(`${zip} removed`);
      await load();
    } catch (e) {
      toast.error("Could not remove");
    } finally {
      setBusy("");
    }
  };

  const downloadTemplate = () => {
    const csv = "zip,label,storage,labor,inbound,outbound\n59001,Billings MT,1.10,16.00,6.50,9.50\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "glogrid-zip-rates-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const res = await bulkImportZips(passcode, text);
      if (res.imported > 0) {
        toast.success(`Imported ${res.imported} zipcode${res.imported === 1 ? "" : "s"}`, {
          description: res.errors.length ? `${res.errors.length} row(s) skipped — see below.` : undefined,
        });
      }
      if (res.errors.length) {
        toast.error(`${res.errors.length} row(s) had errors`, {
          description: res.errors.slice(0, 3).map((x) => `Row ${x.row}: ${x.message}`).join(" · "),
        });
      }
      if (!res.imported && !res.errors.length) toast.info("No rows found in the CSV.");
      await load();
    } catch (err) {
      toast.error("Import failed", { description: err?.response?.data?.detail || "Check the CSV format." });
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="mt-12" data-testid="zip-manager">
      <div className="flex items-center gap-2.5">
        <span className="grid h-7 w-7 place-items-center rounded-sm bg-blue-600 text-white">
          <MapPin className="h-4 w-4" />
        </span>
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
          Locked Zipcode Rates
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Add custom rates for any specific customer zipcode. These override the regional estimate and the pre-listed
        metros for that exact zipcode. Enter <strong>basic</strong> rates — customer quotes add the standard
        {" "}${laborUplift.toFixed(0)}/hr labor loading automatically.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="h-10 rounded-sm border-slate-300 font-semibold text-slate-700 hover:bg-slate-100"
          data-testid="zip-template-btn"
        >
          <Download className="mr-2 h-4 w-4" /> Download CSV template
        </Button>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          className="h-10 rounded-sm bg-blue-600 font-semibold text-white transition-colors hover:bg-blue-700"
          data-testid="zip-import-btn"
        >
          {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          Bulk import CSV
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onImport}
          className="hidden"
          data-testid="zip-import-input"
        />
        <span className="text-xs text-slate-400">Columns: zip, label, storage, labor, inbound, outbound</span>
      </div>

      {/* Add new zip */}
      <div className="mt-5 rounded-sm border border-dashed border-slate-300 bg-white p-5">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Plus className="h-3.5 w-3.5" /> Lock a new zipcode
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Zipcode</Label>
            <Input
              value={add.zip}
              onChange={(e) => setAdd((p) => ({ ...p, zip: e.target.value }))}
              placeholder="e.g. 59001"
              className="h-10 rounded-sm border-slate-300 font-mono-plex focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              data-testid="zip-add-code"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Label (optional)</Label>
            <Input
              value={add.label}
              onChange={(e) => setAdd((p) => ({ ...p, label: e.target.value }))}
              placeholder="Billings, MT"
              className="h-10 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              data-testid="zip-add-label"
            />
          </div>
          {FIELDS.map((f) => (
            <div key={f.k} className="flex flex-col gap-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{f.label}</Label>
              <Input
                type="number"
                min={0}
                step={f.step}
                value={add[f.k]}
                onChange={(e) => setAdd((p) => ({ ...p, [f.k]: num(e.target.value) }))}
                className="h-10 rounded-sm border-slate-300 font-mono-plex focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid={`zip-add-${f.k}`}
              />
            </div>
          ))}
        </div>
        <Button
          onClick={onAdd}
          disabled={adding}
          className="mt-4 h-10 rounded-sm bg-slate-900 px-5 font-semibold text-white transition-colors hover:bg-blue-600"
          data-testid="zip-add-btn"
        >
          {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
          Lock rates
        </Button>
      </div>

      {/* Existing zips */}
      {zips.length === 0 ? (
        <p className="mt-5 rounded-sm border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-400">
          No locked zipcodes yet. Add one above to fix rates for a customer's specific location.
        </p>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-sm border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Zip / Label</th>
                {FIELDS.map((f) => (
                  <th key={f.k} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {f.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {zips.map((z) => (
                <tr key={z.zip} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`zip-row-${z.zip}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono-plex font-semibold text-slate-900">{z.zip}</div>
                    <Input
                      value={draft[z.zip]?.label ?? ""}
                      onChange={(e) => setDraft((p) => ({ ...p, [z.zip]: { ...p[z.zip], label: e.target.value } }))}
                      placeholder="Label"
                      className="mt-1 h-8 w-40 rounded-sm border-slate-200 text-xs focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      data-testid={`zip-label-${z.zip}`}
                    />
                  </td>
                  {FIELDS.map((f) => (
                    <td key={f.k} className="px-4 py-3">
                      <Input
                        type="number"
                        min={0}
                        step={f.step}
                        value={draft[z.zip]?.[f.k] ?? ""}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, [z.zip]: { ...p[z.zip], [f.k]: num(e.target.value) } }))
                        }
                        className="h-9 w-24 rounded-sm border-slate-300 font-mono-plex text-sm focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                        data-testid={`zip-input-${z.zip}-${f.k}`}
                      />
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => onSave(z.zip)}
                        disabled={busy === z.zip}
                        className="h-9 rounded-sm bg-slate-900 px-3 font-semibold text-white transition-colors hover:bg-blue-600"
                        data-testid={`zip-save-${z.zip}`}
                      >
                        {busy === z.zip ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDelete(z.zip)}
                        disabled={busy === z.zip}
                        className="h-9 rounded-sm border-slate-300 px-3 text-red-600 transition-colors hover:bg-red-50"
                        data-testid={`zip-delete-${z.zip}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
